import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const toThreeDecimals = (num) => Number(parseFloat(num).toFixed(3)); // Use for all measurements and calc
const toTwoDecimals = (num) => parseFloat(parseFloat(num).toFixed(2)); // Use for Dollars and Hours
const toFourDecimals = (num) => parseFloat(parseFloat(num).toFixed(4)); // Use for GROSS PROFIT (STORED AS DECIMAL) then 100xed

const SECTION_MARGIN = 12;
const FULL_WIDTH = 515;
const COLUMN_GAP = 20;
const HALF_COLUMN_WITH_GAP = (FULL_WIDTH - COLUMN_GAP) / 2;

const HEADER = () => {
  return [
    {
      columns: [
        {
          image: "logo",
          width: 120,
        },
        {
          text: "Time Summary",
          style: "header",
        },
      ],
      columnGap: 10,
      margin: [0, 0, 0, 8], // margin: [left, top, right, bottom]
    },
  ];
};

const LINE_TOP = () => {
  return [
    {
      canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }],
    },
  ];
};

const LINE_BELOW = () => {
  return [
    {
      canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }],
      margin: [0, 4, 0, 0], // margin: [left, top, right, bottom]
    },
  ];
};

const ENTRIES = (weeklyUserTimeSummary) => {
  if (!weeklyUserTimeSummary) {
    return [];
  }
  const timeEntries = [];
  for (let i = 0; i < weeklyUserTimeSummary.timeEntries.length; i++) {
    let entry = weeklyUserTimeSummary.timeEntries[i];
    timeEntries.push([
      {
        text: `${
          entry.date
            ? dayjs(entry.date).tz(DEFAULT_TIMEZONE).format("MM/DD/YY")
            : ""
        }`,
        style: "tableCell",
      },
      {
        text: `${
          entry.start
            ? dayjs(entry.start).tz(DEFAULT_TIMEZONE).format("hh:mm a")
            : ""
        }`,
        style: "tableCell",
      },
      {
        text: `${
          entry.stop
            ? dayjs(entry.stop).tz(DEFAULT_TIMEZONE).format("hh:mm a")
            : ""
        }`,
        style: "tableCell",
      },
      {
        text: `${entry.lunchDurationMinutes ?? 0}`,
        style: "tableCell",
      },
      {
        text: `${entry.breakMinutes ?? 0}`,
        style: "tableCell",
      },
      {
        text: `${
          entry.workMinutes
            ? toThreeDecimals(Number(entry.workMinutes) / 60)
            : 0
        }`,
        style: "tableCell",
      },
      { text: `${entry.noWork ? "Y" : "N"}`, style: "tableCell" },
      { text: `${entry.noWorkReason ?? ""}`, style: "tableCell" },
    ]);
  }

  return [
    {
      text: `WEEK OF ${dayjs(weeklyUserTimeSummary.start)
        .tz(DEFAULT_TIMEZONE)
        .format("MM/DD/YY")} - ${dayjs(weeklyUserTimeSummary.stop)
        .tz(DEFAULT_TIMEZONE)
        .format("MM/DD/YY")}`,
      style: "subheader",
      margin: [0, SECTION_MARGIN, 0, 0], // margin: [left, top, right, bottom]
    },
    ...LINE_TOP(),
    {
      layout: "noBorders", // optional
      table: {
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        headerRows: 1,
        widths: ["*", "*", "*", "*", "*"],
        body: [
          [
            {
              text: "EMPLOYEE",
              style: "detailName",
            },
            { text: "TOTAL HOURS:", style: "detailName" },
            { text: "NO-WORK DAYS:", style: "detailName" },
            { text: "PHONE NUMBER:", style: "detailName" },
            { text: "EMAIL:", style: "detailName" },
          ],
          [
            {
              text: `${weeklyUserTimeSummary.user.fullName}`,
              style: "detailValue",
            },
            {
              text: `${toThreeDecimals(
                Number(weeklyUserTimeSummary.totalWorkMinutes ?? 0) / 60
              )}`,
              style: "detailValue",
            },
            {
              text: `${Number(weeklyUserTimeSummary.totalNoWorkDays ?? 0)}`,
              style: "detailValue",
            },
            {
              text: `${weeklyUserTimeSummary.user.phone}`,
              style: "detailValue",
            },
            {
              text: `${weeklyUserTimeSummary.user.email}`,
              style: "detailValue",
            },
          ],
        ],
      },
      margin: [0, 4, 0, 0], // margin: [left, top, right, bottom]
    },

    ...LINE_BELOW(),

    {
      text: `TIME ENTRIES`,
      style: "subheader",
      margin: [0, SECTION_MARGIN, 0, 0], // margin: [left, top, right, bottom]
    },
    {
      unbreakable: true,
      table: {
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        headerRows: 1,
        // widths: ["*", 25, 25, 60, 100],
        widths: [40, 40, 40, 40, 40, 40, 50, "*"],

        body: [
          [
            {
              text: "DATE",
              style: "tableHeader",
              fillColor: "#D3D3D3",
            },
            { text: "START", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "STOP", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "LUNCH", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "BREAK", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "HOURS", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "NO WORK", style: "tableHeader", fillColor: "#D3D3D3" },
            { text: "REASON", style: "tableHeader", fillColor: "#D3D3D3" },
          ],
          ...timeEntries,
        ],
      },
      margin: [0, 4, 0, 0], // margin: [left, top, right, bottom]
    },
    {
      text: ``,
      style: "subheader",
      margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
    },
    // ...LINE_BELOW(),
  ];
};

const SIGNATURE = (weeklyUserTimeSummary) => {
  if (weeklyUserTimeSummary.employeeSignedOn) {
    return {
      margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
      unbreakable: true,
      stack: [
        {
          text: `By signing this time sheet I certify that I have carefully reviewed this time sheet and that the hours reported on this time sheet, including all start and stop times, are accurate and I have not reported more or less time than I actually worked.`,
          fontSize: 10,
          alignment: "left",
        },
        {
          margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
          text: [
            {
              text: "Signed By: ",
              bold: true,
              fontSize: 12,
            },
            {
              text: weeklyUserTimeSummary.user.fullName
                ? `${weeklyUserTimeSummary.user.fullName}                              `
                : "                                                                    ",
              fontSize: 12,
              decoration: weeklyUserTimeSummary.user.fullName
                ? null
                : "underline",
            },
          ],
        },
        {
          margin: [0, 6, 0, 0], // margin: [left, top, right, bottom]
          text: [
            {
              text: "Signed On: ",
              bold: true,
              fontSize: 12,
            },
            {
              text: weeklyUserTimeSummary.employeeSignedOn
                ? `${dayjs(weeklyUserTimeSummary.employeeSignedOn)
                    .tz(DEFAULT_TIMEZONE)
                    .format("MM-DD-YY")}                              `
                : "                                                                    ",
              fontSize: 12,
              decoration: weeklyUserTimeSummary.employeeSignedOn
                ? null
                : "underline",
            },
          ],
        },
        weeklyUserTimeSummary.user.fullName
          ? {
              margin: [0, 8, 0, 0], // margin: [left, top, right, bottom]
              stack: [
                {
                  margin: [0, 4, 0, 4], // margin: [left, top, right, bottom]
                  image: "verified",
                  width: 30,
                },
                {
                  text: `${weeklyUserTimeSummary.verificationString}`,
                  fontSize: 8,
                },
                {
                  text: `${weeklyUserTimeSummary.employeeDigitalSignature}`,
                  fontSize: 8,
                },
              ],
            }
          : null,
      ],
    };
  } else {
    return [
      {
        margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
        unbreakable: true,
        stack: [
          [
            {
              text: `By signing this time sheet I certify that I have carefully reviewed this time sheet and that the hours reported on this time sheet, including all start and stop times, are accurate and I have not reported more or less time than I actually worked.`,
              fontSize: 10,
              alignment: "left",
            },
            {
              margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
              columns: [
                {
                  width: "auto",
                  text: "Print Name: ",
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
                {
                  width: "auto",
                  text: `__________________________________`,
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
              ],
            },
            {
              margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
              columns: [
                {
                  width: "auto",
                  text: "Signature: ",
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
                {
                  width: "auto",
                  text: `____________________________________`,
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
              ],
            },

            {
              margin: [0, 10, 0, 0], // margin: [left, top, right, bottom]
              columns: [
                {
                  width: "auto",
                  text: "Date: ",
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
                {
                  width: "auto",
                  text: `_________________________________________`,
                  bold: true,
                  fontSize: 12,
                  alignment: "left",
                },
              ],
            },
          ],
        ],
      },
    ];
  }
};

// NEED TO ADD CACHE BUST TO PREVENT CACHE IMAGE CORS ERROR
// TODO: https://github.com/bpampuch/pdfmake/issues/2416#issuecomment-1291687790
// Instead of Creating a Presigned URL, create aan api call that directly returns the blob or base64
const getBase64FromUrl = async (url) => {
  let fetchResponse = await fetch(url);
  let blob = await fetchResponse.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onerror = (error) => {
      console.log("FAILED CONVERTED BLOB TO BASE64");
      reject(error);
    };
    reader.onloadend = () => {
      const base64data = reader.result;
      console.log("SUCCESSFULLY CONVERTED BLOB TO BASE64");
      resolve(base64data);
    };
  });
};

const getTimeCardDocDefinition = async (
  weeklyUserTimeSummary,
  useUnsignedVersion = false,
  token
) => {
  let content = [
    ...HEADER(),
    ...ENTRIES(weeklyUserTimeSummary),
    SIGNATURE(weeklyUserTimeSummary),
  ];

  let images = {
    checked:
      "data:image/png;base64,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",
    unchecked:
      "data:image/png;base64,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",

    // PREVENT S3 CORS ERROR BY ADDING "?cacheblock=true"
    // https://stackoverflow.com/questions/20253472/cors-problems-with-amazon-s3-on-the-latest-chomium-and-google-canary
    logo: await getBase64FromUrl(
      "https://ezorder-public.s3.us-east-2.amazonaws.com/industry-metal-co-long-logo.png?cacheblock=true"
    ),
    verified: await getBase64FromUrl(
      "https://ezorder-public.s3.us-east-2.amazonaws.com/verified_icon.png?cacheblock=true"
    ),
  };

  var docDefinition = {
    pageSize: "Letter",
    content: content,

    footer: function (currentPage, pageCount) {
      return {
        margin: [40, 10, 40, 0], // margin: [left, top, right, bottom]
        columns: [
          {
            text: `Created on ${dayjs()
              .tz(DEFAULT_TIMEZONE)
              .toDate()
              .toDateString()}`,
            alignment: "left",
            style: "footerText",
          },
          {
            text: currentPage.toString() + " of " + pageCount,
            alignment: "center",
            style: "footerText",
          },
          // {
          //   text: "EZ-ORDER Ver.1.0",
          //   alignment: "right",
          //   style: "footerText",
          // },
        ],
      };
    },

    styles: {
      header: {
        fontSize: 24,
        bold: true,
        alignment: "right",
      },
      subheader: {
        bold: true,
        fontSize: 12,
      },
      imageText: {
        fontSize: 10,
        alignment: "center",
      },
      imageTextBold: {
        bold: true,
        fontSize: 10,
        alignment: "center",
      },
      detailName: {
        bold: true,
        fontSize: 10,
      },
      detailValue: {
        fontSize: 8,
      },
      tableHeader: {
        bold: true,
        fontSize: 10,
        alignment: "left",
      },
      tableCell: {
        fontSize: 8,
        alignment: "left",
      },
      regularText: {
        fontSize: 10,
      },
      materialHeader: {
        fillColor: "#D3D3D3",
        bold: true,
        fontSize: 10,
      },
      checkbox: {},
      footerText: {
        fontSize: 8,
      },
    },
    defaultStyle: {
      fontSize: 12,
    },
    images,
  };

  return docDefinition;
};

export default getTimeCardDocDefinition;
